import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { formatINR } from "@/lib/format";

const emptyLine = () => ({ uid: crypto.randomUUID(), account_id: "", debit: "", credit: "" });

/** One editable debit/credit line of the journal entry. */
function JournalLineRow({ line, index, accounts, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center" data-testid={`je-line-${index}`}>
      <select value={line.account_id} onChange={(e) => onChange({ account_id: e.target.value })} data-testid={`je-account-${index}`} className="col-span-6 bg-white border border-[#D8D4C7] py-2 px-2 text-sm focus:outline-none">
        <option value="">Select account…</option>
        {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
      </select>
      <input type="number" step="0.01" min="0" value={line.debit} onChange={(e) => onChange({ debit: e.target.value, credit: "" })} data-testid={`je-debit-${index}`} className="col-span-3 bg-white border border-[#D8D4C7] py-2 px-2 text-sm text-right num focus:outline-none" placeholder="0" />
      <div className="col-span-3 flex items-center gap-1">
        <input type="number" step="0.01" min="0" value={line.credit} onChange={(e) => onChange({ credit: e.target.value, debit: "" })} data-testid={`je-credit-${index}`} className="flex-1 bg-white border border-[#D8D4C7] py-2 px-2 text-sm text-right num focus:outline-none" placeholder="0" />
        <button type="button" onClick={onRemove} className="text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

/** Modal form to post a balanced manual journal entry. */
export function JournalEntryForm({ accounts, onClose, onSaved }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState("");
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => (ls.length > 2 ? ls.filter((_, idx) => idx !== i) : ls));

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = totalDebit > 0 && Math.abs(totalDebit - totalCredit) < 0.005;

  const submit = async (e) => {
    e.preventDefault();
    if (!balanced) { toast.error("Debits must equal credits"); return; }
    const payload = {
      date: new Date(date).toISOString(),
      narration,
      lines: lines
        .filter((l) => l.account_id && ((parseFloat(l.debit) || 0) > 0 || (parseFloat(l.credit) || 0) > 0))
        .map((l) => ({ account_id: l.account_id, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 })),
    };
    try {
      await api.post("/ledger/journal", payload);
      toast.success("Journal entry posted");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post entry");
    }
  };

  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-50 flex items-center justify-center p-6 fade-in" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="journal-entry-form">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-3xl tracking-tight">New journal entry</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-[#6C7565]" /></button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="je-date" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Narration</span>
            <input value={narration} onChange={(e) => setNarration(e.target.value)} data-testid="je-narration" placeholder="e.g. Owner capital introduced" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
          </label>
        </div>

        <div className="space-y-2 mb-4">
          <div className="grid grid-cols-12 gap-2 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">
            <span className="col-span-6">Account</span>
            <span className="col-span-3 text-right">Debit</span>
            <span className="col-span-3 text-right">Credit</span>
          </div>
          {lines.map((l, i) => (
            <JournalLineRow key={l.uid} line={l} index={i} accounts={accounts} onChange={(patch) => setLine(i, patch)} onRemove={() => removeLine(i)} />
          ))}
          <button type="button" onClick={addLine} data-testid="je-add-line" className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#6C7565] hover:text-[#1A1F16]">
            <Plus className="w-3 h-3" /> Add line
          </button>
        </div>

        <div className={`flex justify-end gap-6 py-2 border-t border-[#D8D4C7] font-mono text-xs mb-6 ${balanced ? "text-[#2E6F40]" : "text-[#D94F3D]"}`} data-testid="je-balance-status">
          <span>Dr {formatINR(totalDebit)}</span>
          <span>Cr {formatINR(totalCredit)}</span>
          <span>{balanced ? "Balanced ✓" : `Diff ${formatINR(Math.abs(totalDebit - totalCredit))}`}</span>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-[#1A1F16] py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
          <button type="submit" data-testid="je-submit" disabled={!balanced} className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628] disabled:opacity-40">Post entry</button>
        </div>
      </form>
    </div>
  );
}
